/**
 * chat-bubble-animation.js
 *
 * Gives the Shopify Inbox chat bubble (bottom-right) the same life as the
 * AI mechanic's welcome hero in the Forty5 customer app: a gentle breathe
 * inside a soft molten-gold glow. No bounce, no flourish — system motion
 * rules (standard easing, calm loop).
 *
 * Widget anatomy (verified against the deployed Inbox extension bundle):
 * the theme-embedded <shopify-chat> element is just a mount marker — the
 * widget script appends a separate <inbox-online-store-chat> custom element
 * with an OPEN shadow root containing the actual bubble button. We animate
 * that button and inject the keyframes into its shadow root (page CSS can't
 * cross the shadow boundary). Animating a structural ancestor instead would
 * risk re-anchoring the widget's fixed positioning (transform/filter create
 * containing blocks).
 *
 * Behavior:
 *   - Polls briefly for the widget (it loads async; bounded at ~30s).
 *   - Stops permanently for the session once the customer interacts with
 *     the bubble (attention cue has done its job).
 *   - Fully disabled under prefers-reduced-motion.
 */

(function () {
  'use strict';

  var DONE_KEY = 'forty5:chat-bubble-animated';
  var STYLE_ID = 'f5-chat-bubble-anim';
  var CLASS = 'f5-chat-breathe';
  var POLL_MS = 400;
  var GIVE_UP_MS = 30000;

  var CSS =
    '@keyframes f5-chat-breathe {' +
    '  0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(255, 182, 39, 0)); }' +
    '  50% { transform: scale(1.045); filter: drop-shadow(0 0 12px rgba(255, 182, 39, 0.55)); }' +
    '}' +
    '.' + CLASS + ' {' +
    '  animation: f5-chat-breathe 2.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;' +
    '  will-change: transform, filter;' +
    '}' +
    '@media (prefers-reduced-motion: reduce) {' +
    '  .' + CLASS + ' { animation: none; }' +
    '}';

  function alreadyDone() {
    try {
      return sessionStorage.getItem(DONE_KEY) === '1';
    } catch (_e) {
      return false;
    }
  }

  function markDone() {
    try {
      sessionStorage.setItem(DONE_KEY, '1');
    } catch (_e) {
      /* private mode — the off state just won't persist */
    }
  }

  function ensureStyle(root) {
    var already = root.querySelector && root.querySelector('#' + STYLE_ID);
    if (already) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    (root === document ? document.head : root).appendChild(style);
  }

  // The Inbox widget appends <inbox-online-store-chat> (open shadow root
  // holding the bubble button). <shopify-chat> is kept as a fallback for
  // older bundle versions.
  function findBubble() {
    var host = document.querySelector('inbox-online-store-chat, shopify-chat');
    if (!host) return null;
    var scope = host.shadowRoot || host;
    var node =
      (scope.querySelector && scope.querySelector('button, [role="button"], iframe')) || null;
    if (node) return node;
    return window.getComputedStyle(host).position === 'fixed' ? host : null;
  }

  function animate() {
    var node = findBubble();
    if (!node) return false;
    var root = node.getRootNode ? node.getRootNode() : document;
    ensureStyle(root);
    node.classList.add(CLASS);
    // First interaction = cue delivered; calm down for the session.
    ['pointerdown', 'focusin'].forEach(function (evt) {
      node.addEventListener(
        evt,
        function () {
          node.classList.remove(CLASS);
          markDone();
        },
        { once: true, capture: true }
      );
    });
    return true;
  }

  function init() {
    if (alreadyDone()) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    if (animate()) return;
    // The widget script loads async and builds its shadow DOM after mount —
    // a bounded poll is the reliable way to catch the finished button.
    var waited = 0;
    var timer = setInterval(function () {
      waited += POLL_MS;
      if (animate() || waited >= GIVE_UP_MS) clearInterval(timer);
    }, POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
